import { v4 as uuidv4 } from 'uuid';
export const MAIN_LIFTS = ['squat', 'bench', 'row', 'ohp', 'deadlift'];
export const LIFT_NAMES = {
    squat: 'Squat',
    bench: 'Bench Press',
    row: 'Barbell Row',
    ohp: 'Overhead Press',
    deadlift: 'Deadlift',
};
/**
 * The programs, as StrongLifts defines them. Deadlift is one heavy set of
 * five in both: it is the most taxing lift and comes last, after squats.
 */
export const FITNESS_PROGRAMS = {
    '5x5': {
        A: [
            { lift: 'squat', sets: 5, reps: 5 },
            { lift: 'bench', sets: 5, reps: 5 },
            { lift: 'row', sets: 5, reps: 5 },
        ],
        B: [
            { lift: 'squat', sets: 5, reps: 5 },
            { lift: 'ohp', sets: 5, reps: 5 },
            { lift: 'deadlift', sets: 1, reps: 5 },
        ],
    },
    '3x5': {
        A: [
            { lift: 'squat', sets: 3, reps: 5 },
            { lift: 'bench', sets: 3, reps: 5 },
            { lift: 'row', sets: 3, reps: 5 },
        ],
        B: [
            { lift: 'squat', sets: 3, reps: 5 },
            { lift: 'ohp', sets: 3, reps: 5 },
            { lift: 'deadlift', sets: 1, reps: 5 },
        ],
    },
};
/**
 * Where a beginner starts: the empty bar for most lifts (the point of the
 * program is to start light and add weight every session), a little more for
 * rows and deadlifts, which start from the floor.
 */
export const DEFAULT_START_WEIGHTS = {
    kg: { squat: 20, bench: 20, row: 30, ohp: 20, deadlift: 40 },
    lb: { squat: 45, bench: 45, row: 65, ohp: 45, deadlift: 95 },
};
/** Added after every successful session. Deadlift moves twice as fast. */
export const DEFAULT_INCREMENTS = {
    kg: { squat: 2.5, bench: 2.5, row: 2.5, ohp: 2.5, deadlift: 5 },
    lb: { squat: 5, bench: 5, row: 5, ohp: 5, deadlift: 10 },
};
export const DEFAULT_BAR_WEIGHT = { kg: 20, lb: 45 };
/** Plate sizes available, per side. The calculator only uses these. */
export const DEFAULT_PLATES = {
    kg: [25, 20, 15, 10, 5, 2.5, 1.25],
    lb: [45, 35, 25, 10, 5, 2.5],
};
export const FITNESS_SETTINGS_ID = 'fitness-settings';
export function createDefaultFitnessSettings(unit = 'kg', now = new Date()) {
    const stamp = now.toISOString();
    return {
        id: FITNESS_SETTINGS_ID,
        unit,
        program: '5x5',
        bar_weight: DEFAULT_BAR_WEIGHT[unit],
        plates: [...DEFAULT_PLATES[unit]],
        start_weights: {},
        increments: {},
        rest_seconds: { success: 90, failed: 300 },
        workout_days: [1, 3, 5],
        weekly_target: 3,
        warmups: true,
        accessories: { A: [], B: [] },
        // Epoch, not now: an untouched default must never win a sync merge
        // against settings the user actually chose on another device.
        created_at: stamp,
        updated_at: new Date(0).toISOString(),
    };
}
/**
 * Fill every field a stored settings row might be missing. Settings written
 * by a later version can carry fields this one does not know; spreading the
 * stored row first keeps them.
 */
export function normalizeFitnessSettings(stored) {
    const unit = stored?.unit === 'lb' ? 'lb' : 'kg';
    const base = createDefaultFitnessSettings(unit);
    if (!stored)
        return base;
    return {
        ...base,
        ...stored,
        id: FITNESS_SETTINGS_ID,
        unit,
        program: stored.program === '3x5' ? '3x5' : '5x5',
        plates: stored.plates?.length ? stored.plates : base.plates,
        start_weights: stored.start_weights ?? {},
        increments: stored.increments ?? {},
        rest_seconds: { ...base.rest_seconds, ...(stored.rest_seconds ?? {}) },
        workout_days: stored.workout_days ?? base.workout_days,
        weekly_target: stored.weekly_target && stored.weekly_target > 0 ? stored.weekly_target : base.weekly_target,
        warmups: stored.warmups ?? true,
        accessories: {
            A: stored.accessories?.A ?? [],
            B: stored.accessories?.B ?? [],
        },
    };
}
export function createFitnessWorkout(fields, now = new Date()) {
    const stamp = now.toISOString();
    const { quick, ...rest } = fields;
    return {
        id: uuidv4(),
        ...rest,
        ...(quick ? { quick: true } : {}),
        status: 'in_progress',
        started_at: stamp,
        finished_at: null,
        notes: '',
        created_at: stamp,
        updated_at: stamp,
    };
}
export function bodyweightId(date) {
    return `bw-${date}`;
}
export function createFitnessBodyweight(date, weight, unit, now = new Date()) {
    const stamp = now.toISOString();
    return { id: bodyweightId(date), date, weight, unit, created_at: stamp, updated_at: stamp };
}
export function liftName(lift) {
    return LIFT_NAMES[lift] ?? lift;
}
/** For one-line summaries: "Squat 62.5 · Press 30 · Deadlift 60". */
export const LIFT_SHORT_NAMES = {
    squat: 'Squat',
    bench: 'Bench',
    row: 'Row',
    ohp: 'Press',
    deadlift: 'Deadlift',
};
/** A main lift's short name; an accessory keeps the name it was given. */
export function liftShortName(lift, name) {
    return LIFT_SHORT_NAMES[lift] ?? name ?? lift;
}
//# sourceMappingURL=fitness.js.map